import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPlans, getProducts, getServices, getTestimonials, getFaqs, getBlogPosts, createOrder, Plan, Product, Service, Testimonial, FAQ, BlogPost } from '../../services/api';
import { IMAGE_BASE_URL } from '../../api/config';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  type: 'plan' | 'product';
  store: 'tech' | 'stationery';
}

export default function HomePage() {
  const navigate = useNavigate();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeStore, setActiveStore] = useState<'tech' | 'stationery'>('tech');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [techProducts, setTechProducts] = useState<Product[]>([]);
  const [stationeryProducts, setStationeryProducts] = useState<Product[]>([]);
  const [stationeryServices, setStationeryServices] = useState<any[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);

  // Pagination State
  const [currentPageTech, setCurrentPageTech] = useState(1);
  const [currentPageStationery, setCurrentPageStationery] = useState(1);
  const ITEMS_PER_PAGE = 9;

  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    phone: '',
    address: ''
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [viewAllCategory, setViewAllCategory] = useState<'tech' | 'stationery' | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          fetchedPlans,
          fetchedProducts,
          fetchedServices,
          fetchedTestimonials,
          fetchedFaqs,
          fetchedBlogPosts
        ] = await Promise.all([
          getPlans(),
          getProducts(),
          getServices(),
          getTestimonials(),
          getFaqs(),
          getBlogPosts()
        ]);

        setPlans(fetchedPlans);

        const tech = fetchedProducts.filter(p => p.category === 'tech');
        setTechProducts(tech);

        const stationery = fetchedProducts.filter(p => p.category === 'stationery');
        setStationeryProducts(stationery);

        const mappedServices = fetchedServices.map(s => ({
          id: s.service_id,
          name: s.name,
          icon: s.icon,
          services: s.sub_services,
          prices: s.price_text
        }));
        setStationeryServices(mappedServices);

        setTestimonials(fetchedTestimonials);
        setFaqs(fetchedFaqs);
        setBlogPosts(fetchedBlogPosts);

      } catch (error) {
        console.error('Failed to fetch data', error);
      }
    };

    fetchData();
  }, []);

  const addToCart = (item: Omit<CartItem, 'quantity'>) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        return prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const getCartTotal = (store: 'tech' | 'stationery') => {
    return cart
      .filter(item => item.store === store)
      .reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getCartItems = (store: 'tech' | 'stationery') => {
    return cart.filter(item => item.store === store);
  };

  const openCheckout = (store: 'tech' | 'stationery') => {
    setActiveStore(store);
    setIsCheckoutOpen(true);
  };

  const handleConfirmOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const items = getCartItems(activeStore);
    const total = getCartTotal(activeStore);

    // 1. Save to Database
    const orderData = {
      customer_name: customerDetails.name,
      customer_phone: customerDetails.phone,
      customer_address: customerDetails.address,
      items: items,
      total: total,
      store: activeStore
    };

    const result = await createOrder(orderData);

    if (result.success) {
      // 2. Redirect to WhatsApp
      const phone = activeStore === 'tech' ? '5219541397099' : '5219541417890';
      const storeName = activeStore === 'tech' ? 'Tecnología' : 'Papelería';

      const messageItems = items.map(item =>
        `${item.name} x${item.quantity} - $${(item.price * item.quantity).toFixed(2)}`
      ).join('\n');

      const fullMessage = `Hola, soy ${customerDetails.name}.\nMe gustaría ordenar de ${storeName}:\n\n${messageItems}\n\nTotal: $${total.toFixed(2)} MXN\n\nDirección: ${customerDetails.address}`;

      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(fullMessage)}`, '_blank');

      // 3. Clear Cart and Close Modal
      setCart(prev => prev.filter(item => item.store !== activeStore));
      setIsCheckoutOpen(false);
      setCustomerDetails({ name: '', phone: '', address: '' });
      setShowCart(false);
    } else {
      alert('Hubo un error al procesar el pedido. Por favor intente nuevamente.');
    }

    setIsSubmitting(false);
  };















  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://static.readdy.ai/image/fcd41dfa5df98c2f0a8b6825ec9e25ef/890511506ba52d1a941f8ddafad7ce2c.png" alt="TecnosMX Logo" className="h-12 w-auto" />
            </div>

            <div className="hidden md:flex items-center gap-1">
              <a href="#inicio" className={`px-4 py-2 text-sm font-medium transition-all rounded-full whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}>Inicio</a>
              <a href="#tecnologia" className={`px-4 py-2 text-sm font-medium transition-all rounded-full whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}>Tecnología</a>
              <a href="#papeleria" className={`px-4 py-2 text-sm font-medium transition-all rounded-full whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}>Papelería</a>
              <a href="#servicios" className={`px-4 py-2 text-sm font-medium transition-all rounded-full whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}>Servicios</a>
              <a href="#blog" className={`px-4 py-2 text-sm font-medium transition-all rounded-full whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}>Blog</a>
              <Link to="/support" className={`px-4 py-2 text-sm font-medium transition-all rounded-full whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}>Soporte</Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-gray-600"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>

            {/* Mobile Menu Overlay */}
            {isMenuOpen && (
              <div className="fixed inset-0 z-50 bg-black/50 md:hidden" onClick={() => setIsMenuOpen(false)}>
                <div className="absolute top-0 right-0 w-3/4 h-full bg-white shadow-xl p-6 flex flex-col gap-6" onClick={e => e.stopPropagation()}>
                  <div className="flex justify-between items-center border-b border-gray-100 pb-4">
                    <span className="text-xl font-bold text-[#2B5BA6]">Menú</span>
                    <button onClick={() => setIsMenuOpen(false)} className="p-2 text-gray-500 hover:bg-gray-50 rounded-full">
                      <i className="ri-close-line text-2xl"></i>
                    </button>
                  </div>
                  <nav className="flex flex-col gap-2">
                    <a href="#inicio" onClick={() => setIsMenuOpen(false)} className="px-4 py-3 bg-gray-50 rounded-xl font-medium text-gray-700 hover:bg-[#2B5BA6]/10 hover:text-[#2B5BA6] transition-colors">Inicio</a>
                    <a href="#tecnologia" onClick={() => setIsMenuOpen(false)} className="px-4 py-3 bg-gray-50 rounded-xl font-medium text-gray-700 hover:bg-[#2B5BA6]/10 hover:text-[#2B5BA6] transition-colors">Tecnología</a>
                    <a href="#papeleria" onClick={() => setIsMenuOpen(false)} className="px-4 py-3 bg-gray-50 rounded-xl font-medium text-gray-700 hover:bg-[#2B5BA6]/10 hover:text-[#2B5BA6] transition-colors">Papelería</a>
                    <a href="#servicios" onClick={() => setIsMenuOpen(false)} className="px-4 py-3 bg-gray-50 rounded-xl font-medium text-gray-700 hover:bg-[#2B5BA6]/10 hover:text-[#2B5BA6] transition-colors">Servicios</a>
                    <a href="#blog" onClick={() => setIsMenuOpen(false)} className="px-4 py-3 bg-gray-50 rounded-xl font-medium text-gray-700 hover:bg-[#2B5BA6]/10 hover:text-[#2B5BA6] transition-colors">Blog</a>
                    <Link to="/support" onClick={() => setIsMenuOpen(false)} className="px-4 py-3 bg-gray-50 rounded-xl font-medium text-gray-700 hover:bg-[#2B5BA6]/10 hover:text-[#2B5BA6] transition-colors">Soporte</Link>
                  </nav>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowCart(!showCart)}
                className="relative p-2 cursor-pointer"
              >
                <i className={`ri-shopping-cart-line text-xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#2B5BA6] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
              </button>
              <a
                href="https://wa.me/5219541397099"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] text-white px-5 py-2 rounded-full text-sm font-medium flex items-center gap-2 hover:bg-[#20BD5A] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCart(false)}></div>
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl">
            <div className="h-full flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Carrito de Compras</h3>
                  <button onClick={() => setShowCart(false)} className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                    <i className="ri-close-line text-2xl text-gray-600"></i>
                  </button>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveStore('tech')}
                    className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${activeStore === 'tech'
                      ? 'bg-[#2B5BA6] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                  >
                    Tecnología ({getCartItems('tech').length})
                  </button>
                  <button
                    onClick={() => setActiveStore('stationery')}
                    className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${activeStore === 'stationery'
                      ? 'bg-[#2B5BA6] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                  >
                    Papelería ({getCartItems('stationery').length})
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {getCartItems(activeStore).length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-shopping-cart-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500">Tu carrito está vacío</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {getCartItems(activeStore).map(item => (
                      <div key={item.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 text-sm">{item.name}</h4>
                          <p className="text-sm text-gray-600">Cantidad: {item.quantity}</p>
                          <p className="text-sm font-bold text-[#2B5BA6]">${(item.price * item.quantity).toFixed(2)} MXN</p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="p-2 hover:bg-red-100 rounded-lg cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-red-500"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {getCartItems(activeStore).length > 0 && (
                <div className="p-6 border-t border-gray-200 space-y-4">
                  <div className="flex items-center justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-[#2B5BA6]">${getCartTotal(activeStore).toFixed(2)} MXN</span>
                  </div>
                  <button
                    onClick={() => openCheckout(activeStore)}
                    className="w-full bg-[#25D366] text-white py-3 rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-[#20BD5A] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-xl"></i>
                    Enviar Pedido por WhatsApp
                  </button>
                  <p className="text-xs text-gray-500 text-center">
                    {activeStore === 'tech'
                      ? 'Se enviará al WhatsApp de Tecnología'
                      : 'Se enviará al WhatsApp de Papelería'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section id="inicio" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20fiber%20optic%20cables%20glowing%20with%20blue%20light%20representing%20high%20speed%20internet%20connectivity%20and%20modern%20telecommunications%20network%20infrastructure%20technology%20with%20bokeh%20effect%20and%20dark%20background%20creating%20professional%20tech%20atmosphere&width=1920&height=1080&seq=hero001&orientation=landscape"
            alt="Hero Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-2xl">
            <div className="inline-block px-4 py-2 border-2 border-[#2B5BA6] bg-white/10 backdrop-blur-sm rounded-full mb-6">
              <span className="text-white text-sm font-medium">Dos Locales para Servirte Mejor</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Tecnología Completa + Papelería Profesional
            </h1>
            <p className="text-base md:text-lg text-white/90 mb-8 leading-relaxed">
              Internet de alta velocidad, equipos de cómputo, cámaras de seguridad y papelería completa con servicios de impresión, fotocopias y recargas
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#tecnologia"
                className="bg-[#2B5BA6] text-white px-8 py-4 rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-[#234a8a] transition-colors whitespace-nowrap cursor-pointer"
              >
                Local de Tecnología
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="#papeleria"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold flex items-center justify-center hover:bg-white hover:text-[#2B5BA6] transition-colors whitespace-nowrap cursor-pointer"
              >
                Local de Papelería
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Store Section */}
      <section id="tecnologia" className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-block px-4 py-2 bg-[#2B5BA6]/10 rounded-full mb-4">
              <span className="text-[#2B5BA6] text-sm font-medium">Local de Tecnología</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Soluciones <span className="text-[#2B5BA6]">Tecnológicas</span>
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Internet de alta velocidad, equipos de cómputo, cámaras de seguridad y soporte técnico profesional
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <i className="ri-phone-line text-[#2B5BA6]"></i>
              <a href="tel:+5219541397099" className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-[#2B5BA6]">
                +52 954 139 7099
              </a>
            </div>
          </div>

          {/* Internet Plans */}
          <div id="planes" className="mb-20">
            <div className="flex flex-col md:flex-row items-center justify-between mb-8 md:mb-12 gap-4 text-center md:text-left">
              <div>
                <h3 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-4">
                  Planes de <span className="text-[#2B5BA6]">Internet</span>
                </h3>
                <p className="text-base md:text-lg text-gray-600">Elige el plan perfecto para tu hogar o negocio</p>
              </div>
              <div className="bg-gray-900 text-white px-6 py-2 rounded-full text-sm font-medium whitespace-nowrap">
                Instalación Incluida
              </div>
            </div>

            <div className="overflow-x-auto pb-8 -mx-6 px-6 scrollbar-hide">
              <div className="flex gap-6 w-max">
                {plans.map((plan, index) => (
                  <div key={plan.id} className="w-[85vw] md:w-96 bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-100 flex flex-col flex-shrink-0 snap-center">
                    <div className="bg-gradient-to-br from-[#4A90E2] to-[#2B5BA6] p-8 text-center relative overflow-hidden">
                      <div className="absolute top-0 right-0 -mr-4 -mt-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
                      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-white/20 rounded-2xl backdrop-blur-sm">
                        <i className="ri-wifi-line text-4xl text-white"></i>
                      </div>
                      <h4 className="text-xl font-bold text-white mb-1">{plan.name}</h4>
                      <p className="text-white/80 text-sm font-medium bg-white/10 inline-block px-3 py-1 rounded-full">{plan.speed}</p>
                    </div>

                    <div className="p-8 flex-1 flex flex-col">
                      <div className="text-center mb-8">
                        <div className="flex items-end justify-center text-gray-900">
                          <span className="text-5xl font-bold tracking-tight">${Number(plan.price)}</span>
                          <span className="text-xl font-bold mb-1">.00</span>
                        </div>
                        <span className="text-gray-500 text-sm font-medium">MXN / mes</span>
                      </div>

                      <ul className="space-y-4 mb-8 flex-1">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-sm text-gray-600">
                            <i className="ri-checkbox-circle-fill text-[#2B5BA6] text-lg mt-0.5"></i>
                            <span className="leading-tight">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-auto">
                        <button
                          onClick={() => addToCart({ id: plan.plan_id, name: plan.name, price: Number(plan.price), type: 'plan', store: 'tech' })}
                          className={`w-full py-4 rounded-xl font-bold transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-blue-900/10 flex items-center justify-center gap-2 cursor-pointer ${index < 2
                            ? 'bg-[#2B5BA6] text-white hover:bg-[#234a8a]'
                            : 'bg-white text-[#2B5BA6] border-2 border-[#2B5BA6] hover:bg-[#2B5BA6]/5'
                            }`}
                        >
                          Contratar Ahora <i className="ri-arrow-right-line"></i>
                        </button>
                        <p className="text-xs text-gray-400 text-center mt-4 font-medium flex items-center justify-center gap-1">
                          <i className="ri-shield-check-line"></i> Instalación Profesional Incluida
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tech Products */}
          <div id="productos-tech" className="mb-20">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
              <div>
                <h3 className="text-4xl font-bold text-gray-900 mb-2">Productos Tecnológicos</h3>
                <p className="text-lg text-gray-600">Equipos de cómputo y cámaras de seguridad</p>
              </div>
              <button
                onClick={() => { setViewAllCategory('tech'); setSearchQuery(''); }}
                className="text-[#2B5BA6] font-bold flex items-center gap-2 hover:gap-3 transition-all self-start md:self-auto"
              >
                Ver Todo <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            <div className="flex overflow-x-auto pb-8 -mx-6 px-6 scrollbar-hide snap-x md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 md:overflow-visible md:pb-0 md:mx-0 md:px-0" data-product-shop>
              {techProducts.slice((currentPageTech - 1) * ITEMS_PER_PAGE, currentPageTech * ITEMS_PER_PAGE).map(product => (
                <div key={product.id} className="w-[85vw] md:w-auto flex-shrink-0 snap-center bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative flex flex-col">
                  {Number(product.stock) === 0 && (
                    <div className="absolute top-4 right-4 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full z-10 shadow-lg">
                      AGOTADO
                    </div>
                  )}
                  <div className="w-full aspect-[4/3] bg-gray-50 relative group overflow-hidden">
                    <img src={product.image.startsWith('http') ? product.image : `${IMAGE_BASE_URL}/${product.image}`} alt={product.name} className={`w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110 ${Number(product.stock) === 0 ? 'opacity-50 grayscale' : ''}`} />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors"></div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="mb-4">
                      <h4 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 min-h-[3.5rem]">{product.name}</h4>
                      <p className="text-sm text-gray-500 line-clamp-2 min-h-[2.5rem]">{product.description}</p>
                    </div>

                    <div className="mt-auto pt-4 border-t border-gray-100">
                      <div className="flex items-end justify-between mb-4">
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-400 font-medium">Precio</span>
                          <span className="text-2xl font-bold text-[#2B5BA6]">${Number(product.price).toFixed(2)}</span>
                        </div>
                        {Number(product.stock) > 0 && Number(product.stock) <= 5 && (
                          <span className="text-orange-500 text-xs font-bold bg-orange-50 px-2 py-1 rounded-md animate-pulse">
                            ¡Solo {product.stock}!
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => addToCart({ id: product.product_id, name: product.name, price: Number(product.price), type: 'product', store: 'tech' })}
                        disabled={Number(product.stock) === 0}
                        className={`w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${Number(product.stock) === 0
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-900 text-white hover:bg-[#2B5BA6] shadow-lg shadow-gray-900/10 hover:shadow-blue-900/20'
                          }`}
                      >
                        <i className={`text-lg ${Number(product.stock) === 0 ? 'ri-close-circle-line' : 'ri-shopping-cart-2-line'}`}></i>
                        {Number(product.stock) === 0 ? 'Agotado' : 'Agregar al Carrito'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Tech Pagination Controls */}
            {techProducts.length > ITEMS_PER_PAGE && (
              <div className="mt-12 flex justify-center items-center gap-4">
                <button
                  onClick={() => setCurrentPageTech(prev => Math.max(prev - 1, 1))}
                  disabled={currentPageTech === 1}
                  className={`p-2 rounded-full border ${currentPageTech === 1 ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-[#2B5BA6] text-[#2B5BA6] hover:bg-[#2B5BA6] hover:text-white cursor-pointer'}`}
                >
                  <i className="ri-arrow-left-s-line text-xl"></i>
                </button>
                <span className="text-gray-600 font-medium">
                  Página {currentPageTech} de {Math.ceil(techProducts.length / ITEMS_PER_PAGE)}
                </span>
                <button
                  onClick={() => setCurrentPageTech(prev => Math.min(prev + 1, Math.ceil(techProducts.length / ITEMS_PER_PAGE)))}
                  disabled={currentPageTech === Math.ceil(techProducts.length / ITEMS_PER_PAGE)}
                  className={`p-2 rounded-full border ${currentPageTech === Math.ceil(techProducts.length / ITEMS_PER_PAGE) ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-[#2B5BA6] text-[#2B5BA6] hover:bg-[#2B5BA6] hover:text-white cursor-pointer'}`}
                >
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Stationery Store Section */}
      <section id="papeleria" className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-block px-4 py-2 bg-[#10B981]/10 rounded-full mb-4">
              <span className="text-[#10B981] text-sm font-medium">Local de Papelería</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Papelería y <span className="text-[#10B981]">Librería Completa</span>
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Todo lo que necesitas para la escuela, oficina y hogar. Impresiones, fotocopias, recargas y más
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <i className="ri-phone-line text-[#10B981]"></i>
              <a href="tel:+5219541417890" className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-[#10B981]">
                +52 954 141 7890
              </a>
            </div>
          </div>

          {/* Stationery Products */}
          <div className="mb-20">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
              <div>
                <h3 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2">Productos de Papelería</h3>
                <p className="text-base md:text-lg text-gray-600">Amplio surtido de artículos escolares y de oficina</p>
              </div>
              <button
                onClick={() => { setViewAllCategory('stationery'); setSearchQuery(''); }}
                className="text-[#10B981] font-bold flex items-center gap-2 hover:gap-3 transition-all self-start md:self-auto"
              >
                Ver Todo <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            <div className="flex overflow-x-auto pb-8 -mx-6 px-6 scrollbar-hide snap-x md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 md:overflow-visible md:pb-0 md:mx-0 md:px-0" data-product-shop>
              {stationeryProducts.slice((currentPageStationery - 1) * ITEMS_PER_PAGE, currentPageStationery * ITEMS_PER_PAGE).map(product => (
                <div key={product.id} className="w-[85vw] md:w-auto flex-shrink-0 snap-center bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative flex flex-col">
                  {Number(product.stock) === 0 && (
                    <div className="absolute top-4 right-4 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full z-10 shadow-lg">
                      AGOTADO
                    </div>
                  )}
                  <div className="w-full aspect-[4/3] bg-gray-50 relative group overflow-hidden">
                    <img src={product.image.startsWith('http') ? product.image : `${IMAGE_BASE_URL}/${product.image}`} alt={product.name} className={`w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110 ${Number(product.stock) === 0 ? 'opacity-50 grayscale' : ''}`} />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors"></div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="mb-4">
                      <h4 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 min-h-[3.5rem]">{product.name}</h4>
                      <p className="text-sm text-gray-500 line-clamp-2 min-h-[2.5rem]">{product.description}</p>
                    </div>

                    <div className="mt-auto pt-4 border-t border-gray-100">
                      <div className="flex items-end justify-between mb-4">
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-400 font-medium">Precio</span>
                          <span className="text-2xl font-bold text-[#10B981]">${Number(product.price).toFixed(2)}</span>
                        </div>
                        {Number(product.stock) > 0 && Number(product.stock) <= 5 && (
                          <span className="text-orange-500 text-xs font-bold bg-orange-50 px-2 py-1 rounded-md animate-pulse">
                            ¡Solo {product.stock}!
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => addToCart({ id: product.product_id, name: product.name, price: Number(product.price), type: 'product', store: 'stationery' })}
                        disabled={Number(product.stock) === 0}
                        className={`w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${Number(product.stock) === 0
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-[#10B981] text-white hover:bg-[#059669] shadow-lg shadow-green-900/10 hover:shadow-green-900/20'
                          }`}
                      >
                        <i className={`text-lg ${Number(product.stock) === 0 ? 'ri-close-circle-line' : 'ri-shopping-cart-2-line'}`}></i>
                        {Number(product.stock) === 0 ? 'Agotado' : 'Agregar al Carrito'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Stationery Pagination Controls */}
            {stationeryProducts.length > ITEMS_PER_PAGE && (
              <div className="mt-12 flex justify-center items-center gap-4">
                <button
                  onClick={() => setCurrentPageStationery(prev => Math.max(prev - 1, 1))}
                  disabled={currentPageStationery === 1}
                  className={`p-2 rounded-full border ${currentPageStationery === 1 ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white cursor-pointer'}`}
                >
                  <i className="ri-arrow-left-s-line text-xl"></i>
                </button>
                <span className="text-gray-600 font-medium">
                  Página {currentPageStationery} de {Math.ceil(stationeryProducts.length / ITEMS_PER_PAGE)}
                </span>
                <button
                  onClick={() => setCurrentPageStationery(prev => Math.min(prev + 1, Math.ceil(stationeryProducts.length / ITEMS_PER_PAGE)))}
                  disabled={currentPageStationery === Math.ceil(stationeryProducts.length / ITEMS_PER_PAGE)}
                  className={`p-2 rounded-full border ${currentPageStationery === Math.ceil(stationeryProducts.length / ITEMS_PER_PAGE) ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white cursor-pointer'}`}
                >
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </button>
              </div>
            )}
          </div>

          {/* Stationery Services */}
          <div>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
              <div>
                <h3 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2">Servicios de Papelería</h3>
                <p className="text-base md:text-lg text-gray-600">Impresiones, fotocopias, recargas y más</p>
              </div>
              <button className="text-[#10B981] font-bold flex items-center gap-2 hover:gap-3 transition-all self-start md:self-auto">
                Ver Todo <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            <div className="flex overflow-x-auto pb-8 -mx-6 px-6 scrollbar-hide snap-x flex-nowrap md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stationeryServices.map(service => (
                <div key={service.id} className="w-[85vw] sm:w-auto flex-shrink-0 snap-center bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                  <div className="w-16 h-16 bg-[#10B981]/10 rounded-2xl flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-4xl text-[#10B981]`}></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4">{service.name}</h4>
                  <ul className="space-y-2 mb-6">
                    {service.services.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-check-line text-[#10B981]"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm font-bold text-[#10B981]">{service.prices}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div className="text-left">
              <div className="inline-block px-4 py-2 bg-[#2B5BA6]/10 rounded-full mb-4">
                <span className="text-[#2B5BA6] text-sm font-medium">Servicios Profesionales</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-2">
                Nuestros <span className="text-[#2B5BA6]">Servicios</span>
              </h2>
              <p className="text-base md:text-lg text-gray-600 max-w-2xl">
                Soluciones tecnológicas completas para tu hogar o negocio
              </p>
            </div>
            <button className="text-[#2B5BA6] font-bold flex items-center gap-2 hover:gap-3 transition-all self-start md:self-auto whitespace-nowrap">
              Ver Todo <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          <div className="flex overflow-x-auto pb-8 -mx-6 px-6 scrollbar-hide snap-x flex-nowrap md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Mantenimiento de Computadoras */}
            <div className="w-[85vw] md:w-auto flex-shrink-0 snap-center bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 border border-gray-100">
              <div className="w-full h-48 mb-6 rounded-2xl overflow-hidden bg-gray-100">
                <img
                  src="https://readdy.ai/api/search-image?query=computer%20technician%20performing%20maintenance%20and%20cleaning%20on%20desktop%20PC%20tower%20with%20tools%20and%20equipment%20professional%20IT%20support%20service%20hardware%20repair%20and%20optimization%20clean%20white%20background&width=400&height=300&seq=maint001&orientation=landscape"
                  alt="Mantenimiento de Computadoras"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-16 h-16 bg-[#2B5BA6]/10 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-tools-line text-4xl text-[#2B5BA6]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Mantenimiento de Computadoras</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Mantenimiento preventivo y correctivo para mantener tu equipo funcionando como nuevo. Limpieza, optimización y actualización de software.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Limpieza física profunda
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Optimización de software
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Eliminación de virus
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Respaldo de información
                </li>
              </ul>
              <a
                href="https://wa.me/5219541397099?text=Hola,%20necesito%20servicio%20de%20mantenimiento%20de%20computadoras"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-[#2B5BA6] text-white py-3 rounded-full font-semibold hover:bg-[#234a8a] transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                Solicitar Servicio
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Instalación de Cámaras */}
            <div className="w-[85vw] md:w-auto flex-shrink-0 snap-center bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 border border-gray-100">
              <div className="w-full h-48 mb-6 rounded-2xl overflow-hidden bg-gray-100">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20security%20camera%20installation%20technician%20mounting%20surveillance%20camera%20on%20wall%20with%20tools%20modern%20CCTV%20system%20setup%20for%20home%20and%20business%20protection%20clean%20white%20background&width=400&height=300&seq=camera001&orientation=landscape"
                  alt="Instalación de Cámaras"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-16 h-16 bg-[#2B5BA6]/10 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-camera-line text-4xl text-[#2B5BA6]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Instalación de Cámaras de Seguridad</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Instalación profesional de sistemas de videovigilancia con acceso remoto. Protege tu hogar o negocio las 24 horas.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Evaluación del sitio
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Instalación profesional
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Configuración de acceso remoto
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Capacitación incluida
                </li>
              </ul>
              <a
                href="https://wa.me/5219541397099?text=Hola,%20necesito%20instalación%20de%20cámaras%20de%20seguridad"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-[#2B5BA6] text-white py-3 rounded-full font-semibold hover:bg-[#234a8a] transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                Solicitar Servicio
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Desarrollo de Sistemas */}
            <div className="w-[85vw] md:w-auto flex-shrink-0 snap-center bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 border border-gray-100">
              <div className="w-full h-48 mb-6 rounded-2xl overflow-hidden bg-gray-100">
                <img
                  src="https://readdy.ai/api/search-image?query=software%20developer%20programming%20custom%20business%20application%20on%20computer%20screen%20with%20code%20and%20modern%20interface%20design%20professional%20web%20development%20and%20system%20creation%20clean%20white%20background&width=400&height=300&seq=dev001&orientation=landscape"
                  alt="Desarrollo de Sistemas"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-16 h-16 bg-[#2B5BA6]/10 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-code-s-slash-line text-4xl text-[#2B5BA6]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Desarrollo de Sistemas Personalizados</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Creamos sistemas y aplicaciones a medida para tu negocio. Automatiza procesos y mejora la eficiencia de tu empresa.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Sistemas web personalizados
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Aplicaciones de gestión
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Automatización de procesos
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Soporte y mantenimiento
                </li>
              </ul>
              <a
                href="https://wa.me/5219541397099?text=Hola,%20necesito%20desarrollo%20de%20un%20sistema%20personalizado"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-[#2B5BA6] text-white py-3 rounded-full font-semibold hover:bg-[#234a8a] transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                Solicitar Servicio
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Soporte Técnico 24/7 */}
            <div className="w-[85vw] md:w-auto flex-shrink-0 snap-center bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 border border-gray-100">
              <div className="w-full h-48 mb-6 rounded-2xl overflow-hidden bg-gray-100">
                <img
                  src="https://readdy.ai/api/search-image?query=IT%20support%20technician%20helping%20customer%20with%20computer%20problem%20remote%20technical%20assistance%20and%20troubleshooting%20service%20professional%20help%20desk%20support%20clean%20white%20background&width=400&height=300&seq=support001&orientation=landscape"
                  alt="Soporte Técnico"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-16 h-16 bg-[#2B5BA6]/10 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-customer-service-2-line text-4xl text-[#2B5BA6]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Soporte Técnico 24/7</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Asistencia técnica disponible las 24 horas del día. Resolvemos tus problemas tecnológicos de forma rápida y eficiente.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Atención inmediata
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Soporte remoto y presencial
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Resolución de problemas
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Asesoría técnica
                </li>
              </ul>
              <a
                href="https://wa.me/5219541397099?text=Hola,%20necesito%20soporte%20técnico"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-[#2B5BA6] text-white py-3 rounded-full font-semibold hover:bg-[#234a8a] transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                Solicitar Soporte
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Reparación de Equipos */}
            <div className="w-[85vw] md:w-auto flex-shrink-0 snap-center bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 border border-gray-100">
              <div className="w-full h-48 mb-6 rounded-2xl overflow-hidden bg-gray-100">
                <img
                  src="https://readdy.ai/api/search-image?query=technician%20repairing%20laptop%20computer%20with%20screwdriver%20and%20tools%20professional%20electronics%20repair%20service%20fixing%20hardware%20components%20clean%20white%20background&width=400&height=300&seq=repair001&orientation=landscape"
                  alt="Reparación de Equipos"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-16 h-16 bg-[#2B5BA6]/10 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-settings-3-line text-4xl text-[#2B5BA6]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Reparación de Equipos</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Reparamos computadoras, laptops y equipos electrónicos. Diagnóstico gratuito y presupuesto sin compromiso.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Diagnóstico gratuito
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Reparación de hardware
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Cambio de componentes
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Garantía en reparaciones
                </li>
              </ul>
              <a
                href="https://wa.me/5219541397099?text=Hola,%20necesito%20reparación%20de%20equipo"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-[#2B5BA6] text-white py-3 rounded-full font-semibold hover:bg-[#234a8a] transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                Solicitar Servicio
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Configuración de Redes */}
            <div className="w-[85vw] md:w-auto flex-shrink-0 snap-center bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 border border-gray-100">
              <div className="w-full h-48 mb-6 rounded-2xl overflow-hidden bg-gray-100">
                <img
                  src="https://readdy.ai/api/search-image?query=network%20technician%20configuring%20wifi%20router%20and%20ethernet%20cables%20professional%20networking%20setup%20for%20office%20and%20home%20internet%20connectivity%20clean%20white%20background&width=400&height=300&seq=network001&orientation=landscape"
                  alt="Configuración de Redes"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-16 h-16 bg-[#2B5BA6]/10 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-router-line text-4xl text-[#2B5BA6]"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Configuración de Redes</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Instalación y configuración de redes cableadas e inalámbricas. Optimización de señal WiFi y seguridad de red.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Instalación de redes
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Optimización WiFi
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Seguridad de red
                </li>
                <li className="flex items-center gap-3 text-sm text-gray-700">
                  <i className="ri-check-line text-[#2B5BA6] text-lg"></i>
                  Configuración de routers
                </li>
              </ul>
              <a
                href="https://wa.me/5219541397099?text=Hola,%20necesito%20configuración%20de%20red"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-[#2B5BA6] text-white py-3 rounded-full font-semibold hover:bg-[#234a8a] transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                Solicitar Servicio
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section >

      {/* Testimonials Section */}
      < section id="testimonios" className="py-20 bg-white" >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <span className="text-[#2B5BA6] text-sm font-medium flex items-center gap-2 mb-2">
                <span className="w-2 h-2 bg-[#2B5BA6] rounded-full"></span>
                Testimonios
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Lo que dicen nuestros clientes</h2>
            </div>
            <button className="text-[#2B5BA6] font-bold flex items-center gap-2 hover:gap-3 transition-all self-start md:self-auto">
              Ver Todo <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          <div className="flex overflow-x-auto pb-8 -mx-6 px-6 scrollbar-hide snap-x flex-nowrap md:grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="w-[85vw] md:w-auto flex-shrink-0 snap-center bg-gray-50 p-8 rounded-3xl">
                <div className="mb-6">
                  <i className="ri-double-quotes-l text-5xl text-[#2B5BA6]"></i>
                </div>
                <p className="text-lg text-gray-800 mb-6 leading-relaxed">
                  {testimonial.text}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#2B5BA6] rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    {testimonial.verified && (
                      <div className="flex items-center gap-2">
                        <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <i className="ri-star-fill"></i>
                          Cliente Verificado
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-gray-900 mb-4">
            Preguntas <span className="text-[#2B5BA6]">Frecuentes</span>
          </h2>
          <p className="text-lg text-gray-600 mb-12">Resolvemos tus dudas más comunes</p>

          <div className="grid md:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-2xl">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section id="blog" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-2">
                Blog de <span className="text-[#2B5BA6]">Tecnología</span>
              </h2>
              <p className="text-lg text-gray-600">Mantente informado sobre las últimas tendencias</p>
            </div>
            <button className="text-[#2B5BA6] font-semibold flex items-center gap-2 hover:gap-3 transition-all self-start md:self-auto">
              Ver Todo <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          <div className="flex overflow-x-auto pb-8 -mx-6 px-6 scrollbar-hide snap-x flex-nowrap md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {blogPosts.map(post => (
              <div
                key={post.id}
                onClick={() => navigate(`/blog?id=${post.id}`)}
                className="w-[80vw] sm:w-auto flex-shrink-0 snap-center bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
              >
                <div className="w-full h-48">
                  <img src={post.image.startsWith('http') ? post.image : `${IMAGE_BASE_URL}/${post.image}`} alt={post.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-6">
                  <span className="inline-block bg-[#2B5BA6] text-white text-xs px-3 py-1 rounded-full mb-3">
                    {post.category}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">{post.title}</h3>
                  <button className="text-[#2B5BA6] font-semibold text-sm flex items-center gap-2 hover:gap-3 transition-all whitespace-nowrap cursor-pointer">
                    Leer más
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a2332] text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 1200 600">
            <path d="M0 300 Q 300 200 600 300 T 1200 300" stroke="currentColor" fill="none" strokeWidth="2" />
            <path d="M0 350 Q 300 250 600 350 T 1200 350" stroke="currentColor" fill="none" strokeWidth="2" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-5 gap-12 mb-12">
            <div className="md:col-span-2">
              <img src="https://static.readdy.ai/image/fcd41dfa5df98c2f0a8b6825ec9e25ef/890511506ba52d1a941f8ddafad7ce2c.png" alt="TecnosMX" className="h-12 mb-6" />
              <p className="text-2xl font-serif text-white mb-6 leading-relaxed">
                Mantente actualizado con nuestras ofertas
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="tu@email.com"
                  className="flex-1 bg-transparent border-2 border-white/30 rounded-full px-6 py-3 text-white placeholder-white/50 focus:outline-none focus:border-white"
                />
                <button className="w-12 h-12 bg-[#2B5BA6] rounded-full flex items-center justify-center hover:bg-[#234a8a] transition-colors cursor-pointer">
                  <i className="ri-notification-line text-xl"></i>
                </button>
              </div>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Servicios</h4>
              <ul className="space-y-2">
                <li><a href="#planes" className="text-white/70 hover:text-white transition-colors cursor-pointer">Internet</a></li>
                <li><a href="#productos" className="text-white/70 hover:text-white transition-colors cursor-pointer">Cámaras</a></li>
                <li><a href="#productos" className="text-white/70 hover:text-white transition-colors cursor-pointer">Computación</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Productos</h4>
              <ul className="space-y-2">
                <li><a href="#productos" className="text-white/70 hover:text-white transition-colors cursor-pointer">Papelería</a></li>
                <li><a href="#productos" className="text-white/70 hover:text-white transition-colors cursor-pointer">Impresiones</a></li>
                <li><a href="#productos" className="text-white/70 hover:text-white transition-colors cursor-pointer">Recargas</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Soporte</h4>
              <ul className="space-y-2">
                <li><a href="#faq" className="text-white/70 hover:text-white transition-colors cursor-pointer">FAQ</a></li>
                <li><a href="#blog" className="text-white/70 hover:text-white transition-colors cursor-pointer">Blog</a></li>
                <li><a href="mailto:tecnosjuquila16@gmail.com" className="text-white/70 hover:text-white transition-colors cursor-pointer">Contacto</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-white/70 text-sm">© 2025 TecnosMX. Todos los derechos reservados</p>

            <div className="flex items-center gap-4">
              <a href="#" className="w-10 h-10 border-2 border-white/30 rounded-lg flex items-center justify-center hover:border-white transition-colors cursor-pointer">
                <i className="ri-facebook-fill"></i>
              </a>
              <a href="#" className="w-10 h-10 border-2 border-white/30 rounded-lg flex items-center justify-center hover:border-white transition-colors cursor-pointer">
                <i className="ri-instagram-line"></i>
              </a>
              <a href="https://wa.me/5219541397099" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border-2 border-white/30 rounded-lg flex items-center justify-center hover:border-white transition-colors cursor-pointer">
                <i className="ri-whatsapp-line"></i>
              </a>
              <a href="mailto:tecnosjuquila16@gmail.com" className="w-10 h-10 border-2 border-white/30 rounded-lg flex items-center justify-center hover:border-white transition-colors cursor-pointer">
                <i className="ri-mail-line"></i>
              </a>
            </div>

            <a href="https://abisoftgt.net" target="_blank" rel="noopener noreferrer" className="text-white/70 text-sm hover:text-white transition-colors cursor-pointer">
              Powered by AbisoftGT
            </a>
          </div>
        </div>
      </footer >
      {/* Checkout Modal */}
      {
        isCheckoutOpen && (
          <div className="fixed inset-0 z-[60] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setIsCheckoutOpen(false)}>
                <div className="absolute inset-0 bg-black/50"></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-[#2B5BA6]/10 sm:mx-0 sm:h-10 sm:w-10">
                      <i className="ri-shopping-bag-3-line text-[#2B5BA6] text-xl"></i>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                        Completar Pedido
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 mb-4">
                          Por favor ingresa tus datos para finalizar el pedido y enviarlo por WhatsApp.
                        </p>

                        <form id="checkout-form" onSubmit={handleConfirmOrder} className="space-y-4">
                          <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nombre Completo</label>
                            <input
                              type="text"
                              id="name"
                              required
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#2B5BA6] focus:border-[#2B5BA6] sm:text-sm"
                              value={customerDetails.name}
                              onChange={(e) => setCustomerDetails({ ...customerDetails, name: e.target.value })}
                            />
                          </div>

                          <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Teléfono</label>
                            <input
                              type="tel"
                              id="phone"
                              required
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#2B5BA6] focus:border-[#2B5BA6] sm:text-sm"
                              value={customerDetails.phone}
                              onChange={(e) => setCustomerDetails({ ...customerDetails, phone: e.target.value })}
                            />
                          </div>

                          <div>
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700">Dirección de Entrega</label>
                            <textarea
                              id="address"
                              rows={3}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#2B5BA6] focus:border-[#2B5BA6] sm:text-sm"
                              value={customerDetails.address}
                              onChange={(e) => setCustomerDetails({ ...customerDetails, address: e.target.value })}
                            />
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    form="checkout-form"
                    disabled={isSubmitting}
                    className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#2B5BA6] text-base font-medium text-white hover:bg-[#234a8a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2B5BA6] sm:ml-3 sm:w-auto sm:text-sm ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {isSubmitting ? 'Procesando...' : 'Confirmar Pedido'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm cursor-pointer"
                    onClick={() => setIsCheckoutOpen(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {/* View All Modal */}
      {
        viewAllCategory && (
          <div className="fixed inset-0 z-[70] overflow-y-auto bg-white animate-in slide-in-from-bottom duration-300">
            <div className="min-h-screen px-4 py-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 sticky top-0 bg-white z-10 py-2 border-b border-gray-100">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                    {viewAllCategory === 'tech' ? 'Productos Tecnológicos' : 'Papelería y Librería'}
                  </h2>
                  <button
                    onClick={() => setViewAllCategory(null)}
                    className="p-2 hover:bg-gray-100 rounded-full cursor-pointer"
                  >
                    <i className="ri-close-line text-3xl text-gray-600"></i>
                  </button>
                </div>

                {/* Search */}
                <div className="mb-8 relative sticky top-[73px] z-10 bg-white pb-4">
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl transform -mt-2"></i>
                  <input
                    type="text"
                    placeholder="Buscar productos..."
                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent focus:bg-white focus:border-[#2B5BA6] rounded-2xl transition-all outline-none text-lg shadow-sm"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                  />
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-20">
                  {(viewAllCategory === 'tech' ? techProducts : stationeryProducts)
                    .filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.description.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map(product => (
                      <div key={product.id} className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col">
                        <div className="aspect-[4/3] bg-gray-50 relative">
                          <img src={product.image.startsWith('http') ? product.image : `${IMAGE_BASE_URL}/${product.image}`} alt={product.name} className="w-full h-full object-cover" />
                          {Number(product.stock) === 0 && (
                            <div className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">AGOTADO</div>
                          )}
                        </div>
                        <div className="p-4 flex-1 flex flex-col">
                          <h4 className="font-bold text-gray-900 mb-1 line-clamp-2">{product.name}</h4>
                          <p className="text-sm text-gray-500 line-clamp-2 mb-3">{product.description}</p>
                          <div className="mt-auto flex items-center justify-between">
                            <span className={`text-lg font-bold ${viewAllCategory === 'tech' ? 'text-[#2B5BA6]' : 'text-[#10B981]'}`}>${Number(product.price).toFixed(2)}</span>
                            <button
                              onClick={() => {
                                addToCart({ id: product.product_id, name: product.name, price: Number(product.price), type: 'product', store: viewAllCategory });
                                setViewAllCategory(null);
                                setShowCart(true);
                              }}
                              disabled={Number(product.stock) === 0}
                              className={`px-4 py-2 rounded-lg font-medium text-sm text-white transition-opacity ${Number(product.stock) === 0 ? 'bg-gray-300' : (viewAllCategory === 'tech' ? 'bg-[#2B5BA6] active:bg-[#1e4075]' : 'bg-[#10B981] active:bg-[#059669]')}`}
                            >
                              {Number(product.stock) === 0 ? 'Agotado' : 'Agregar'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                  {(viewAllCategory === 'tech' ? techProducts : stationeryProducts).filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.description.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
                    <div className="col-span-full text-center py-12 text-gray-500">
                      <i className="ri-search-2-line text-4xl mb-2 block text-gray-300"></i>
                      No se encontraron productos
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
}